import columns from './columns'
import dates from './dates'
import numbers from './numbers'

const filterService = {
  areDatesInArrayInRange(dateRange, itemDate) {
    const [start, end] = dateRange

    if (dateRange.length && start && end) {
      return dates.areDatesInRange(itemDate, start, end)
    }

    return false
  },
  areDatesInObjectInRange(itemDate, dateRange) {
    const { start: startRange, end: endRange } = dateRange

    if (startRange && endRange) {
      return dates.areDatesInRange(itemDate, startRange, endRange)
    }

    return false
  },
  areIncludedInDateRange(itemDate, dateRange) {
    if (Array.isArray(dateRange)) {
      return this.areDatesInArrayInRange(dateRange, itemDate)
    }

    if (dateRange.start || dateRange.end) {
      return this.areDatesInObjectInRange(itemDate, dateRange)
    }

    return false
  },

  isDateIncluded(itemDate, filterDateParameter) {
    const { operator, value: filterValue } = filterDateParameter
    const isString = typeof filterValue[0] === 'string'
    const [filterStartDate, endDate] = isString ? filterValue : filterValue[0]

    if (operator === 'eq') {
      return dates.areDatesEqual(itemDate, filterStartDate)
    }
    if (operator === 'gte') {
      return dates.areDatesGte(itemDate, filterStartDate)
    }
    if (operator === 'lte') {
      return dates.areDatesLte(itemDate, filterStartDate)
    }
    if (operator === 'range') {
      const filterEndDate = endDate || filterStartDate
      const filterDateRange = [filterStartDate, filterEndDate]
      return this.areIncludedInDateRange(itemDate, filterDateRange)
    }

    return true
  },

  normalizeNumber(value, isFilter, isPercentage) {
    if (value === 'N/A') return 'n/a'

    const isNumber = typeof value === 'number'
    const isPercentWithSymbol = !isNumber && value.indexOf('%') !== -1
    const numericalValue = (isNumber && value) || Number(value.replace(/\$|%|,/g, ''))
    const calcPercentage = (number) => {
      const calculate = isFilter || isPercentWithSymbol
      return calculate ? number / 100 : number
    }
    const getPercentage = (number) => isPercentage && calcPercentage(number)

    return getPercentage(numericalValue) || numericalValue
  },
  normalizeString(stringValue) {
    return stringValue.trim().toLowerCase()
  },
  normalizeValue(propertyName, isFilter) {
    const isPercentage = columns.isTypePercentage(propertyName)
    const isNumerical = isPercentage || columns.isTypeNumerical(propertyName)
    const normalizer = (isNumerical && this.normalizeNumber) || this.normalizeString

    return (value) => normalizer(value, isFilter, isPercentage)
  },
  stringify(value) {
    const setEmptyValues = () => !value && String(value)
    const getCustomField = () => value.cf_name && `${String(value.cf_name).trim().toLowerCase()} = ${String(value.cf_value).trim().toLowerCase()}`
    const setString = () => typeof value !== 'string' && String(value)
    return setEmptyValues() || getCustomField() || setString() || value || ''
  },
  normalizeValueByType(rawValue, propertyName, isFilter) {
    const normalizer = (val) => this.normalizeValue(propertyName, isFilter)(val)

    const normalizeArray = () => rawValue && rawValue instanceof Array && rawValue.map((r) => normalizer(this.stringify(r)))
    const normalizeObject = () => rawValue && typeof rawValue === 'object' && Object.keys(rawValue).reduce((normalizedObject, rawValueKey) => {
      if (typeof rawValue[rawValueKey] !== 'string') {
        normalizedObject[rawValueKey] = this.normalizeValueByType(rawValue[rawValueKey], rawValueKey, isFilter)
      } else {
        normalizedObject[rawValueKey] = normalizer(rawValue[rawValueKey])
      }
      return normalizedObject
    }, {})
    const normalizeString = () => normalizer(this.stringify(rawValue))

    return normalizeArray() || normalizeObject() || normalizeString()
  },
  getDeepPropertyValue(fullPropertyName, item) {
    const propertyNames = fullPropertyName.split('.')
    const topLevelPropertyName = propertyNames[0]
    const subLevelPropertyNames = propertyNames.slice(1)

    return subLevelPropertyNames.reduce((propertyValue, subLevelPropertyName) => {
      const currentLevelIsArray = subLevelPropertyName.indexOf('[]') !== -1
      const currentPropName = (currentLevelIsArray && subLevelPropertyName.replace('[]', '')) || subLevelPropertyName

      if (Array.isArray(propertyValue)) {
        propertyValue = propertyValue.map((value) => value[currentPropName])
      } else {
        propertyValue = propertyValue && propertyValue[currentPropName]
      }
      return propertyValue
    }, item[topLevelPropertyName])
  },
  getPropertyNames(propertyName) {
    const [itemPropertyName, filterPropertyName] = propertyName.split(':')
    return { itemPropertyName, filterPropertyName: filterPropertyName || itemPropertyName }
  },
  getPropertyValue(propertyName, item) {
    const isFlatPropertyName = propertyName.indexOf('.') === -1
    const getFlatPropertyValue = () => isFlatPropertyName && item[propertyName]
    return getFlatPropertyValue() || this.getDeepPropertyValue(propertyName, item)
  },
  normalize(propertyName, isFilter) {
    return (item) => {
      const getPropertyValue = () => ({ value: this.getPropertyValue(propertyName, isFilter ? item : item.normalized) })
      const getNormalizedValue = () => !isFilter && item.normalized && getPropertyValue()
      const makeNormalizedValue = () => {
        const raw = isFilter ? item.value : this.getPropertyValue(propertyName, item)

        return {
          operator: item.operator || null,
          value: this.normalizeValueByType(raw, propertyName, isFilter),
        }
      }

      return getNormalizedValue() || makeNormalizedValue()
    }
  },
  normalizeFilterValues(filterPropertyName, filterData) {
    const normalizeValues = this.normalize(filterPropertyName, true)
    return filterData[filterPropertyName].map(normalizeValues)
  },
  isStrictMatch(filterValue, itemValue) {
    return filterValue === itemValue
  },
  isSubstringMatch(filterValue, itemValue) {
    return itemValue?.indexOf(filterValue) !== -1
  },
  isMatch(filterValue, itemValue) {
    return this.isStrictMatch(filterValue, itemValue)
       || this.isSubstringMatch(filterValue, itemValue)
  },
  exclude(useAndToExclude, itemNormalized) {
    const { value: valueNormalized } = itemNormalized

    return (isExcluded, prop) => {
      const { operator, value: filterValueNormalized } = prop
      const exclude = (itemValueNormalized) => {
        if (useAndToExclude) {
          if (['excl'].indexOf(operator) !== -1) {
            isExcluded = isExcluded || this.isMatch(filterValueNormalized, itemValueNormalized)
          } else if (['neq', 'not'].includes(operator)) {
            isExcluded = isExcluded || filterValueNormalized === itemValueNormalized
          }
        } else {
          if (isExcluded) return isExcluded

          if (['excl'].indexOf(operator) !== -1) {
            isExcluded = isExcluded || this.isMatch(filterValueNormalized, itemValueNormalized)
          } else if (['neq', 'not'].indexOf(operator) !== -1) {
            isExcluded = filterValueNormalized === itemValueNormalized
          }
        }

        return isExcluded
      }

      const runExclude = (val) => exclude(val)

      if (valueNormalized instanceof Array) {
        valueNormalized.forEach(runExclude)
      } else {
        runExclude(valueNormalized)
      }

      return isExcluded
    }
  },
  include(useAndToInclude, itemNormalized) {
    const { value: valueNormalized } = itemNormalized

    return (matches, prop) => {
      const { operator, value: filterValueNormalized } = prop

      const include = (itemValueNormalized) => {
        if (useAndToInclude) {
          if (matches === null) {
            matches = true
          }

          if (['incl'].indexOf(operator) !== -1) {
            matches = matches
              && this.isMatch(filterValueNormalized, itemValueNormalized)
          } else if (['eq', 'is'].indexOf(operator) !== -1) {
            matches = matches && filterValueNormalized === itemValueNormalized
          } else if (['gte', 'lte', 'range'].indexOf(operator) !== -1) {
            matches = matches && numbers[operator](itemValueNormalized, filterValueNormalized)
          }
        } else {
          if (matches) return matches

          if (['incl'].indexOf(operator) !== -1) {
            matches = this.isMatch(filterValueNormalized, itemValueNormalized)
          } else if (['eq', 'is'].indexOf(operator) !== -1) {
            matches = filterValueNormalized === itemValueNormalized
          } else if (['gte', 'lte', 'range'].indexOf(operator) !== -1) {
            matches = numbers[operator](itemValueNormalized, filterValueNormalized)
          }
        }

        return matches
      }

      const runInclude = (val) => include(val)

      if (valueNormalized instanceof Array) {
        valueNormalized.forEach(runInclude)
      } else {
        runInclude(valueNormalized)
      }

      return matches
    }
  },
  groupByOperator(options, filterOption) {
    const excluded = ['excl', 'neq', 'not'].find((op) => op === filterOption.operator)

    if (excluded) {
      options.excluded.push(filterOption)
    } else {
      options.included.push(filterOption)
    }

    return options
  },
  compare(propertyName, item, filterData) {
    const { itemPropertyName, filterPropertyName } = this.getPropertyNames(propertyName)

    const normalizedItemValue = this.normalize(itemPropertyName, false)(item)
    const normalizedFilterValues = this.normalizeFilterValues(filterPropertyName, filterData)

    const seedMatches = { excluded: [], included: [] }
    const filtersGrouped = normalizedFilterValues.reduce(this.groupByOperator, seedMatches)

    const useAndToExclude = filtersGrouped.excluded.length > 1
    const useAndToInclude = columns.isTypeNumerical(filterPropertyName)

    const include = this.include(useAndToInclude, normalizedItemValue)
    const exclude = this.exclude(useAndToExclude, normalizedItemValue)

    const includeItem = filtersGrouped.included.reduce(include, null)
    const excludeItem = filtersGrouped.excluded.reduce(exclude, null)

    const haveExcluded = !!filtersGrouped.excluded.length
    const haveIncluded = !!filtersGrouped.included.length

    if (haveExcluded) {
      return haveIncluded ? !excludeItem && includeItem : !excludeItem
    }

    return includeItem
  },

  report(item, filterData, filterKeys) {
    return filterKeys.reduce((isItemIncluded, filterKey) => {
      const keyType = (columns.isTypeDate(filterKey) && 'date') || 'string'
      const currentFilter = filterData[filterKey]
      const getValuesMap = {
        date: {
          isFilterSet: () => currentFilter.length,
          matchesFound: () => {
            // One OR transforms all conditions on this filterKey into an OR statement.
            const includeByDefault = !currentFilter.find((param) => param.conjunction === 'OR')
            return currentFilter.reduce((isIncluded, filterParameter) => {
              const { conjunction } = filterParameter
              const isDateIncluded = this.isDateIncluded(item[filterKey], filterParameter)
              return conjunction === 'OR' ? isIncluded || isDateIncluded : isIncluded && isDateIncluded
            }, includeByDefault)
          },
        },
        string: {
          isFilterSet: () => currentFilter.length,
          matchesFound: (filterProperty) => this.compare(filterProperty, item, filterData),
        },
      }
      const getter = getValuesMap[keyType]
      const isFilterSet = getter.isFilterSet()

      if (isFilterSet) {
        const matchesFound = getter.matchesFound(filterKey)

        isItemIncluded = isItemIncluded && matchesFound
      }

      return isItemIncluded
    }, true)
  },
}

export default filterService
